import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Logo } from "@the-drop/ui";
import {
  getColoradoTodayYmd,
  isAtLeastRecreationalAgeColorado,
  minRecreationalAge,
} from "../lib/colorado-calendar-age";
import { setAgeGateSatisfiedForSlug } from "../lib/age-gate-session";

const APP_ROOT_ID = "root";

export function AgeGateScreen(props: {
  dispensarySlug: string;
  onVerified: () => void;
}) {
  const [dob, setDob] = useState("");
  const [error, setError] = useState<string | null>(null);

  const maxDate = useMemo(() => getColoradoTodayYmd(), []);

  /**
   * `role="dialog"` + `aria-modal="true"` is a WAI-ARIA contract: tab order
   * must NOT escape into the underlying page. We set `inert` on the React
   * mount root, then portal the gate to `<body>` so it sits OUTSIDE the
   * inert subtree and stays interactive. Without this, screen readers can
   * Tab past the form and interact with the obscured-but-still-DOM page
   * behind the gate (redline 07 flag 4 — mandatory, not deferred).
   */
  useEffect(() => {
    const root = document.getElementById(APP_ROOT_ID);
    if (!root) return;
    root.setAttribute("inert", "");
    return () => {
      root.removeAttribute("inert");
    };
  }, []);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!dob || dob.length < 10) {
      setError("Enter your date of birth.");
      return;
    }
    if (dob > maxDate) {
      setError("That date is in the future.");
      return;
    }
    if (!isAtLeastRecreationalAgeColorado(dob)) {
      setError(`You must be ${minRecreationalAge()} or older to continue.`);
      return;
    }
    setAgeGateSatisfiedForSlug(props.dispensarySlug);
    props.onVerified();
  }

  const gate = (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center px-6 text-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      style={{
        background:
          "radial-gradient(circle at 50% 30%, var(--surface-sage) 0%, var(--green-pale) 100%)",
      }}
    >
      <div className="w-full max-w-md space-y-6 rounded-2xl border border-[var(--card-border)] bg-white p-8 shadow-cardMd">
        <div className="flex justify-center">
          <Logo size="md" tone="dark" />
        </div>
        <div className="space-y-2 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">
            21+ verification
          </p>
          <h1
            id="age-gate-title"
            className="text-2xl font-bold tracking-tight text-[var(--text-primary)]"
          >
            Confirm your age
          </h1>
          <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
            Cannabis delivery in Colorado is for adults {minRecreationalAge()}+. Enter your date of
            birth — browser-only, this stays on your device for this session.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 text-left">
          <label htmlFor="dob" className="block">
            <span className="block pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              Date of birth
            </span>
            <input
              id="dob"
              name="dob"
              type="date"
              max={maxDate}
              required
              value={dob}
              onChange={(ev) => setDob(ev.target.value)}
              className="w-full border-0 border-b border-[var(--card-border)] bg-transparent px-0 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--green)] focus:outline-none"
              autoFocus
            />
          </label>
          {error ? (
            <p
              role="alert"
              className="inline-flex items-center rounded-full bg-[var(--status-error-bg)] px-3 py-1 text-xs font-medium text-[var(--status-error-text)]"
            >
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="w-full rounded-lg bg-[var(--green)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
          >
            Continue
          </button>
        </form>
        <p className="text-center text-xs text-[var(--text-muted)]">
          Drop is a Colorado cannabis delivery marketplace. Must be {minRecreationalAge()}+ to order.
          We don't store your date of birth.
        </p>
      </div>
    </div>
  );

  /** SSR-safe: only portal when document is available (Vite CSR always is,
   *  but vitest's jsdom may run this in environments where it's absent). */
  if (typeof document === "undefined") return gate;
  return createPortal(gate, document.body);
}
